import { CardDetailsType } from '@islandsstofa/react'
import { beautifyDate } from '@src/utils/date'

export interface Capital extends CardDetailsType {
  types: string[]
  amount: string
  body: any
  customTags: string[]
}

export const capitalResolver = (node: any): Capital => {
  return {
    prismicId: node.prismicId,
    name: node.data.title,
    image: node.data.logo,
    types: node.data.types,
    amount: node.data.amount,
    tags: node.tags,
    url: node.url,
    body: node.body,
    category: 'Fjármögnun',
    excerpt: node.data.text_on_card,
    customTags: [
      node.data.amount,
      ...node.tags,
      ...node.data.types.map((t: any) => t.type)
    ]
  }
}

export interface Company extends CardDetailsType {
  body: any
  customTags: string[]
}

export const companyResolver = (node: any): Company => {
  return {
    prismicId: node.prismicId,
    name: node.data.title,
    image: node.data.logo,
    tags: node.tags,
    url: node.url,
    body: node.body,
    excerpt: node.data.text_on_card,
    customTags: node.tags,
    category: 'Fyrirtæki'
  }
}

export interface Support extends CardDetailsType {
  prismicId?: string
  url: string
  name: {
    text: string
  }
  excerpt: {
    html: string
  }
  image: {
    url: string
    alt: string
  }
  tags: string[]
  body: any[]
  customTags: string[]
}

export const supportResolver = (node: any): Support => {
  return {
    prismicId: node.prismicId,
    name: node.data.title,
    image: node.data.logo,
    tags: node.tags,
    url: node.url,
    body: node.data.body,
    excerpt: node.data.text_on_card,
    customTags: node.tags,
    category: 'Aðstoð'
  }
}

export interface Opportunity extends CardDetailsType {
  thumbnail: {
    url: string
    alt: string
  }
  date: Date
  type: string
  body: any
  amount: string
  capitals: Capital[]
  events: Event[]
  customTags: string[]
  meta: {
    property: string
    content: string
  }[]
}

export interface Event extends CardDetailsType {
  thumbnail: {
    url: string
    alt: string
  }
  date: Date
  type: string
  body: any
  cost: string
  opportunities: Opportunity[]
  customTags: string[]
  meta: {
    property: string
    content: string
  }[]
}

interface MetaInterface {
  property: string
  content: string
}

const metaResolver = (
  image: { url: string } | null,
  description: string | null
): MetaInterface[] => {
  let meta: MetaInterface[] = []
  if (description) {
    meta.push({ property: 'description', content: description })
    meta.push({ property: 'og:description', content: description })
  }
  if (image) {
    meta.push({ property: 'og:image', content: image.url })
    meta.push({ property: 'twitter:image', content: image.url })
  }
  return meta
}

export const opportunityResolver = (node: any): Opportunity => ({
  ...node,
  ...node.data,
  name: node.data.title,
  thumbnail: node.data.image.thumbnails.thumbnail,
  date: new Date(node.data.dagsetning),
  customTags: [
    `${node.data.amount ? `${node.data.amount} / ` : ''}${beautifyDate(
      new Date(node.data.dagsetning)
    )}`,
    node.data.type,
    ...node.tags
  ].filter(node => node),
  excerpt: node.data.text_on_card,
  category: 'Tækifæri',
  capitals: node.data.capitals
    .filter((c: any) => c.capital.document)
    .map((c: any) => capitalResolver(c.capital.document)),
  events: node.data.events
    ? node.data.events
        .filter((e: any) => e.event.document)
        .map((e: any) =>
          eventResolver({
            ...e.event.document,
            data: { ...e.event.document.data, opportunities: [] }
          })
        )
    : [],
  meta: metaResolver(node.data.override_image, node.data.override_description)
})

export const eventResolver = (node: any): Event => ({
  ...node,
  ...node.data,
  name: node.data.title,
  thumbnail: node.data.image.thumbnails.thumbnail,
  date: new Date(node.data.dagsetning),
  type: node.data.type,
  category: 'Viðburður',
  customTags: [
    `${node.data.cost ? `${node.data.cost} / ` : ''}${beautifyDate(
      new Date(node.data.dagsetning)
    )}`,
    node.data.type,
    ...node.tags
  ].filter(node => node),
  excerpt: node.data.text_on_card,
  opportunities: node.data.opportunities
    .filter((c: any) => c.opportunity.document)
    .map((c: any) => opportunityResolver(c.opportunity.document)),
  meta: metaResolver(node.data.override_image, node.data.override_description)
})

export type RegionKey =
  | 'nordurland-vestra'
  | 'nordurland-eystra'
  | 'vestfirdir'
  | 'vesturland'
  | 'hofudborgarsvaedid'
  | 'reykjanes'
  | 'sudurland'
  | 'austurland'
export interface Region {
  title: {
    text: string
  }
  region: {
    key: RegionKey
    label: string
  }
  text: {
    html: string
  }
  image: {
    alt: string
    url: string
  }
  website: {
    url: string
  }
}

export const regionKeyTitleMap: { [key in RegionKey | string]: string } = {
  vesturland: 'Vesturland',
  vestfirdir: 'Vestfirðir',
  'nordurland-vestra': 'Norðurland-vestra',
  'nordurland-eystra': 'Norðurland-eystra',
  austurland: 'Austurland',
  sudurland: 'Suðurland',
  reykjanes: 'Reykjanes',
  hofudborgarsvaedid: 'Höfuðborgarsvæðið'
}

export const regionResolver = (node: {
  data: {
    region: RegionKey
    titill: { text: string }
    text: { html: string }
    image: { alt: string; url: string }
    website: { url: string }
  }
}): Region => {
  return {
    title: node.data.titill,
    region: {
      key: node.data.region,
      label: regionKeyTitleMap[node.data.region]
    },
    text: node.data.text,
    image: node.data.image,
    website: node.data.website
  }
}
